import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IPreviewResultRowDTO,
  EPreviewFilterType,
  EOlderThanUnit,
  EPreviewFilterField,
  IArchivingJobExperimentResultDTO,
  ArchivingJobExperimentStatusDto,
} from 'business/models/Models';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import {
  previewArchivingThunk,
  runArchivingThunk,
  fetchActiveArchivingJobResultsThunk,
  fetchUndismissedArchivingJobResultsThunk,
} from 'redux/thunks/archiving';
import {
  getArchiveCurrentLocation,
  getArchivedExperimentIds,
  markArchivedResults,
  markPreviewRowsArchived,
  mergeArchivingResults,
} from 'redux/utils/archivingProgressUtils';
import { upsertBackgroundJob } from './backgroundJobsReducer';

type PreviewArchiving = {
  data: IPreviewResultRowDTO[];
  isLoading: boolean;
  error: string | null;
};

type PreviewFiltersState = {
  filterType: EPreviewFilterType;
  filterField: EPreviewFilterField;
  olderThanValue: number | null;
  olderThanUnit: EOlderThanUnit | null;
  startDate: string | null;
  endDate: string | null;
  isPreviewFiltered: boolean;
  isFilterChangedAfterPreview: boolean;
};

type ArchivingState = {
  previewArchiving: PreviewArchiving;
  previewFilters: PreviewFiltersState;
  archivingResults: IArchivingJobExperimentResultDTO[];
};

const initialFilters: PreviewFiltersState = {
  filterType: EPreviewFilterType.OlderThan,
  filterField: EPreviewFilterField.None,
  olderThanValue: 1,
  olderThanUnit: EOlderThanUnit.Years,
  startDate: null,
  endDate: null,
  isPreviewFiltered: false,
  isFilterChangedAfterPreview: false,
};

const initialState: ArchivingState = {
  previewArchiving: {
    data: [],
    isLoading: false,
    error: null,
  } as PreviewArchiving,
  previewFilters: initialFilters,
  archivingResults: [],
};

type PreviewFilterValueKey = Exclude<
  keyof PreviewFiltersState,
  'isPreviewFiltered' | 'isFilterChangedAfterPreview'
>;

const applyPreviewFilterChange = <K extends PreviewFilterValueKey>(
  state: ArchivingState,
  key: K,
  value: PreviewFiltersState[K],
) => {
  if (state.previewFilters[key] === value) {
    return;
  }
  state.previewFilters[key] = value;
  if (state.previewFilters.isPreviewFiltered) {
    state.previewFilters.isFilterChangedAfterPreview = true;
  }
};

const ArchivingSlice = createSlice({
  name: 'archiving',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(previewArchivingThunk.pending, (state) => {
      state.previewArchiving.isLoading = true;
      state.previewArchiving.error = null;
    });

    builder.addCase(previewArchivingThunk.fulfilled, (state, action) => {
      state.previewArchiving.data = action.payload;
      state.previewArchiving.isLoading = false;
      state.previewArchiving.error = null;
    });

    builder.addCase(previewArchivingThunk.rejected, (state, action) => {
      state.previewArchiving.isLoading = false;
      state.previewArchiving.error =
        (action.payload as string) || 'Failed to fetch preview archiving data';
      state.previewArchiving.data = [];
    });

    builder.addCase(runArchivingThunk.fulfilled, (state, action) => {
      state.archivingResults = mergeArchivingResults(
        state.archivingResults,
        action.payload.value,
      );
    });

    builder.addCase(fetchActiveArchivingJobResultsThunk.fulfilled, (state, action) => {
      if (action.payload.value != null) {
        state.archivingResults = action.payload.value;
      }
    });

    builder.addCase(
      fetchUndismissedArchivingJobResultsThunk.fulfilled,
      (state, action) => {
        state.archivingResults = action.payload.value ?? [];
      },
    );

    builder.addMatcher(
      (action): action is PayloadAction<BackgroundJobDTO> =>
        action.type === upsertBackgroundJob.type,
      (state, action) => {
        if (Number(action.payload.jobType) !== JobTypeDTO.Archiving) {
          return;
        }
        const archivedExperimentIds = getArchivedExperimentIds(action.payload.dataObj);
        const currentLocation = getArchiveCurrentLocation(action.payload.dataObj);
        markPreviewRowsArchived(state.previewArchiving.data, archivedExperimentIds);
        markArchivedResults(
          state.archivingResults,
          archivedExperimentIds,
          currentLocation,
        );
      },
    );
  },
  reducers: {
    setFilterType(state, action: PayloadAction<EPreviewFilterType>) {
      applyPreviewFilterChange(state, 'filterType', action.payload);
    },
    setFilterField(state, action: PayloadAction<EPreviewFilterField | null>) {
      applyPreviewFilterChange(
        state,
        'filterField',
        action.payload ?? EPreviewFilterField.None,
      );
    },
    setOlderThanValue(state, action: PayloadAction<number | null>) {
      applyPreviewFilterChange(state, 'olderThanValue', action.payload);
    },
    setOlderThanUnit(state, action: PayloadAction<EOlderThanUnit | null>) {
      applyPreviewFilterChange(state, 'olderThanUnit', action.payload);
    },
    setStartDate(state, action: PayloadAction<string | null>) {
      applyPreviewFilterChange(state, 'startDate', action.payload);
    },
    setEndDate(state, action: PayloadAction<string | null>) {
      applyPreviewFilterChange(state, 'endDate', action.payload);
    },
    setIsPreviewFiltered(state, action: PayloadAction<boolean>) {
      state.previewFilters.isPreviewFiltered = action.payload;
    },
    setIsFilterChangedAfterPreview(state, action: PayloadAction<boolean>) {
      state.previewFilters.isFilterChangedAfterPreview = action.payload;
    },
    setPreviewLoading(state) {
      // Marks the preview as loading as soon as a new tree selection is detected,
      // ahead of the debounced fetch, so the previously selected node's rows don't
      // flash on screen while the new (possibly empty) selection is still fetching.
      state.previewArchiving.isLoading = true;
    },
    removePreviewRowsByTreeNodeId(state, action: PayloadAction<number[]>) {
      const idsToRemove = new Set(action.payload);
      state.previewArchiving.data = state.previewArchiving.data.filter(
        (row) => !idsToRemove.has(row.treeNodeId),
      );
    },
    resetPreviewFilters(state) {
      state.previewFilters = initialFilters;
    },
    clearArchivingResults(state) {
      // Terminal rows (Archived and Failed) are dismissed. Scheduled/Running stay visible
      // because the job is still in progress.
      state.archivingResults = state.archivingResults.filter(
        (row) =>
          row.status !== ArchivingJobExperimentStatusDto.Completed &&
          row.status !== ArchivingJobExperimentStatusDto.Failed,
      );
    },
    resetArchivingResults(state) {
      state.archivingResults = [];
    },
  },
});

export const {
  setFilterType,
  setFilterField,
  setOlderThanValue,
  setOlderThanUnit,
  setStartDate,
  setEndDate,
  setIsPreviewFiltered,
  setIsFilterChangedAfterPreview,
  setPreviewLoading,
  removePreviewRowsByTreeNodeId,
  resetPreviewFilters,
  clearArchivingResults,
  resetArchivingResults,
} = ArchivingSlice.actions;

export default ArchivingSlice.reducer;
